'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { useWebphone } from './use-webphone';
import {
  DEFAULT_INCOMING_CALL_UI_MODE,
  useIncomingCallUiMode,
  type IncomingCallUiMode,
} from './use-incoming-call-ui-mode';

type WebphoneContextValue = ReturnType<typeof useWebphone>;

const WebphoneContext = createContext<WebphoneContextValue | null>(null);

/**
 * #402 — modo de exibição da chamada entrante.
 *
 * Contexto SEPARADO do webphone de propósito: o valor é do tenant, chega por
 * HTTP, e não tem nada a ver com o ciclo de vida do registro SIP. Misturar os
 * dois faria toda mudança de estado do ramal invalidar o consumidor do modo
 * (e vice-versa).
 *
 * O default do contexto é `DEFAULT_INCOMING_CALL_UI_MODE` — hoje `discreet` —,
 * então quem consumir fora do provider (teste isolado, componente montado
 * noutra árvore) cai no mesmo modo de quem nunca configurou, em vez de estourar.
 * Deliberadamente a CONSTANTE e não um literal: o teste do provider compara com
 * o valor real via `vi.importActual` justamente pra este ponto não envelhecer
 * calado quando o padrão mudar de novo.
 */
const IncomingCallUiModeContext = createContext<IncomingCallUiMode>(
  DEFAULT_INCOMING_CALL_UI_MODE,
);

/**
 * Instancia UMA única vez o useWebphone() (um registro SIP por app) e
 * compartilha via contexto. Montado no dashboard layout quando a voz está
 * habilitada. Antes, cada componente que chamasse useWebphone() criaria um
 * Registerer próprio — o provider evita isso e deixa a página do supervisor
 * ler o estado de registro sem registrar de novo.
 *
 * O modo de chamada entrante (#402) é lido aqui pelo mesmo motivo: uma
 * instância, uma leitura por aba. O hook se auto-gateia por voz habilitada +
 * permissão, então em tenant sem telefonia isto não gera request nenhum.
 */
export function WebphoneProvider({ children }: { children: ReactNode }) {
  const wp = useWebphone();
  const incomingCallUiMode = useIncomingCallUiMode();
  return (
    <WebphoneContext.Provider value={wp}>
      <IncomingCallUiModeContext.Provider value={incomingCallUiMode}>
        {children}
      </IncomingCallUiModeContext.Provider>
    </WebphoneContext.Provider>
  );
}

/** Acesso ao webphone compartilhado. Lança fora do provider. */
export function useWebphoneContext(): WebphoneContextValue {
  const ctx = useContext(WebphoneContext);
  if (!ctx) {
    throw new Error('useWebphoneContext deve ser usado dentro de <WebphoneProvider>');
  }
  return ctx;
}

/**
 * Modo de exibição da chamada entrante (#402). Fora do provider devolve
 * `DEFAULT_INCOMING_CALL_UI_MODE` — o mesmo modo de quem nunca configurou.
 */
export function useIncomingCallUiModeContext(): IncomingCallUiMode {
  return useContext(IncomingCallUiModeContext);
}

/**
 * Seletor leve pra componentes que só precisam saber se o webphone está
 * registrado e em qual ramal (ex: gating do botão Puxar). Tolera ausência de
 * provider (voz desligada) retornando não-registrado.
 */
export function useWebphoneRegistration(): {
  isRegistered: boolean;
  extension: string | null;
} {
  const ctx = useContext(WebphoneContext);
  if (!ctx) return { isRegistered: false, extension: null };
  return { isRegistered: ctx.state === 'registered', extension: ctx.extension };
}
